// src/supabaseClient.js
import { createClient } from '@supabase/supabase-js';

/**
 * Supabase Client Configuration
 *
 * Ondersteunt zowel de nieuwe publishable key (sb_publishable_...) als de legacy anon key.
 * De nieuwe publishable key heeft voordelen:
 * - Onafhankelijk te roteren zonder downtime
 * - Betere security (wordt automatisch geblokkeerd in onveilige contexten)
 * - Geen JWT expiry problemen
 *
 * @see https://supabase.com/docs/guides/api/api-keys
 */

// Supabase URL (verplicht)
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

// API Key: Prefereer nieuwe publishable key, fallback naar legacy anon key
// De publishable key (sb_publishable_...) is de nieuwe standaard
// De anon key (eyJ...) is de legacy JWT-based key
const supabaseApiKey =
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;

// Valideer environment variables
if (!supabaseUrl) {
  console.error('[Supabase] VITE_SUPABASE_URL is niet geconfigureerd');
  throw new Error('Missing VITE_SUPABASE_URL environment variable');
}

if (!supabaseApiKey) {
  console.error(
    '[Supabase] Geen API key geconfigureerd. Gebruik VITE_SUPABASE_PUBLISHABLE_KEY (aanbevolen) of VITE_SUPABASE_ANON_KEY (legacy)',
  );
  throw new Error(
    'Missing Supabase API key. Set VITE_SUPABASE_PUBLISHABLE_KEY or VITE_SUPABASE_ANON_KEY',
  );
}

// Waarschuw in development als nog de legacy anon key wordt gebruikt
const isLegacyAnonKey = supabaseApiKey.startsWith('eyJ');

if (import.meta.env.DEV && isLegacyAnonKey) {
  console.warn(
    '[Supabase] Using legacy anon key. Consider migrating to publishable key for better security.',
  );
}

// Supabase client configuratie met security en stability best practices
const supabaseOptions = {
  auth: {
    // Gebruik localStorage voor session persistence (stabiel over page refreshes)
    persistSession: true,
    storageKey: 'bem-auth-token',
    // Auto refresh token voor expiry
    autoRefreshToken: true,
    // Detecteer sessie in andere tabs
    detectSessionInUrl: true,
    // PKCE flow voor betere security
    flowType: 'pkce',
  },
  global: {
    // Request headers voor debugging en identificatie
    headers: {
      'x-client-info': 'bem-frontend/1.0',
    },
  },
  // Realtime configuratie voor betere stability
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
  // Database configuratie
  db: {
    schema: 'public',
  },
};

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseApiKey, supabaseOptions);

/**
 * Helper om de huidige auth sessie op te halen
 * @returns {Promise<Session|null>}
 */
export const getAuthSession = async () => {
  try {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();
    if (error) throw error;
    return session;
  } catch (error) {
    console.error('[Supabase] Error getting auth session:', error);
    return null;
  }
};

/**
 * Helper om te checken of de gebruiker is ingelogd
 * @returns {Promise<boolean>}
 */
export const isAuthenticated = async () => {
  const session = await getAuthSession();
  return session !== null;
};

/**
 * Helper om de huidige user op te halen
 * @returns {Promise<User|null>}
 */
export const getCurrentUser = async () => {
  try {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  } catch (error) {
    console.error('[Supabase] Error getting current user:', error);
    return null;
  }
};
